import { FC, useState } from 'react'
import {
  Text,
  CustomerCard,
  GreyGrey10,
  GreyGrey30,
  GreyGrey40,
  GreyGrey60,
  GreyGrey70,
  GreyGrey90,
  IconBuildings,
  IconCircleInfo,
  IndigoIndigo60,
  Link,
  Primary13Regular,
  Primary13SemiBold,
  SwitchLineField,
  Tooltip,
  TooltipLegacy,
  Box,
  Flex
} from '@sequencehq/core-components'
import { WidgetSection } from 'modules/Cube/view/common/atomic/WidgetSection'
import { useCustomerWidget } from 'modules/Cube/view/layouts/billingSchedule/widgets/Customer/useCustomerWidget'
import { getLogoSrc } from 'Customer/utils/logo'
import { match } from 'ts-pattern'
import { Customer } from 'modules/Cube/domain/cube.domain.types'
import { getPrimaryBillingContact } from 'modules/CustomerContacts'
import { CustomerSelect } from 'Customer/index'
import { CreateCustomerForm } from 'modules/Customers/components/drawers/CreateCustomer/CreateCustomerForm'
import { useQuery } from '@sequencehq/api'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'

export const CustomerWidget: FC = () => {
  const {
    showEditButton,
    editButtonDisabled,
    editButtonDisabledReason,
    view,
    updateView,
    fieldConfig,
    customer,
    createCustomer
  } = useCustomerWidget()

  return (
    <WidgetSection
      title="Customer"
      hideTitle={view === 'customerView'}
      width="100%"
      options={
        <>
          {showEditButton &&
            (editButtonDisabled ? (
              <Tooltip
                label={editButtonDisabledReason}
                shouldWrapChildren={false}
              >
                <Text
                  cursor="not-allowed"
                  data-testid="customer.edit.disabled"
                  color={GreyGrey40}
                >
                  Edit
                </Text>
              </Tooltip>
            ) : (
              <Text
                onClick={() => updateView('customerSelection')}
                _hover={{ cursor: 'pointer' }}
                data-testid="customer.edit"
                color={IndigoIndigo60}
              >
                Edit
              </Text>
            ))}
          {view === 'customerSelection' && customer && (
            <Text
              textStyle="link"
              onClick={() => updateView('customerView')}
              _hover={{
                cursor: 'pointer'
              }}
            >
              Cancel
            </Text>
          )}
        </>
      }
    >
      {match(view)
        .with('customerView', () =>
          customer ? (
            <CustomerView customer={customer} createCustomer={createCustomer} />
          ) : null
        )
        .with('customerSelection', () => (
          <CustomerSelection fieldConfig={fieldConfig} />
        ))
        .exhaustive()}

      {!fieldConfig.rollUpBilling.hidden && (
        <Flex
          data-testid="schedule.rollUpBilling.section"
          padding="12px 16px"
          margin="0 24px"
          marginBottom="20px"
          backgroundColor={GreyGrey10}
          borderRadius="6px"
          alignItems="center"
          justifyContent="space-between"
          flex={1}
        >
          <Flex alignItems="center" gap="4px" justifyContent="start">
            <SwitchLineField
              data-testid="customer.rollUpBilling"
              text={
                <Text {...Primary13Regular}>Enable roll-up billing for </Text>
              }
              {...fieldConfig.rollUpBilling}
            />

            <TooltipLegacy
              style={{
                wrapper: {
                  display: 'inline-block',
                  borderBottom: `1px dashed ${GreyGrey70}`,
                  padding: '4px 0 3px',
                  color: GreyGrey70,
                  transition: '250ms',
                  cursor: 'help',
                  _hover: {
                    color: IndigoIndigo60,
                    borderBottom: `1px dashed ${IndigoIndigo60}`
                  },
                  whiteSpace: 'nowrap'
                },
                content: {
                  padding: '0'
                }
              }}
              content={
                <Box>
                  <Flex
                    backgroundColor={GreyGrey10}
                    color={GreyGrey70}
                    borderRadius="8px"
                    borderBottom={GreyGrey30}
                    padding="8px 16px"
                    alignItems="center"
                    gap="8px"
                    {...Primary13SemiBold}
                  >
                    <IconBuildings />
                    Child{' '}
                    {fieldConfig.rollUpBilling.childAccountCount > 1
                      ? 'customers'
                      : 'customer'}
                  </Flex>
                  <Flex flexDirection="column" gap="8px" padding="12px 16px">
                    {fieldConfig.rollUpBilling.childAccounts?.map(alias => (
                      <Link.Unstyled
                        key={alias}
                        {...Primary13SemiBold}
                        color={GreyGrey90}
                        to={`/customers/${customer?.id}`}
                      >
                        {alias}
                      </Link.Unstyled>
                    ))}
                  </Flex>
                </Box>
              }
            >
              <Text {...Primary13Regular} color={GreyGrey70}>
                {`${fieldConfig.rollUpBilling.childAccountCount} ${
                  fieldConfig.rollUpBilling.childAccountCount > 1
                    ? 'customers'
                    : 'customer'
                }`}
              </Text>
            </TooltipLegacy>
          </Flex>

          <TooltipLegacy
            content={
              <Text>
                Use roll-up billing to bill a parent customer for any child
                customer's usage.{' '}
                <Link
                  to="https://docs.sequencehq.com/billing/roll-up-billing"
                  isExternal
                  {...Primary13SemiBold}
                  color="indigo.50"
                  display="inline-block"
                >
                  Learn more
                </Link>
              </Text>
            }
          >
            <IconCircleInfo color={GreyGrey60} />
          </TooltipLegacy>
        </Flex>
      )}
    </WidgetSection>
  )
}

const CustomerView: FC<
  Pick<ReturnType<typeof useCustomerWidget>, 'createCustomer'> & {
    customer: Customer
  }
> = ({ customer, createCustomer }) => {
  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    {
      id: customer?.id ?? ''
    },
    {
      enabled: Boolean(customer?.id)
    }
  )
  const [showCreateCustomerDrawer, setShowCreateCustomerDrawer] =
    useState(false)

  return (
    <>
      <Box p={4} mt="-36px" pointerEvents="none">
        <Box pointerEvents="auto" w="320px">
          <CustomerCard
            legalName={customer.legalName}
            taxStatus={customer.taxStatus}
            customerId={customer.id}
            address={customer.address ?? undefined}
            billingEmail={
              getPrimaryBillingContact(customerQuery.data?.contacts ?? [])
                ?.email
            }
            logoSrc={getLogoSrc(customer)}
          />
        </Box>
      </Box>
      {showCreateCustomerDrawer && (
        <CreateCustomerForm
          onClose={() => setShowCreateCustomerDrawer(false)}
          onSuccess={() => setShowCreateCustomerDrawer(false)}
          prefill={customer}
          createCustomer={createCustomer}
        />
      )}
    </>
  )
}

const CustomerSelection: FC<
  Pick<ReturnType<typeof useCustomerWidget>, 'fieldConfig'>
> = ({ fieldConfig }) => {
  return (
    <Flex flexDirection="column" px="24px" pb="20px">
      <Box width="376px">
        <CustomerSelect
          data-testid="customer.select"
          value={fieldConfig.customer.value}
          onChange={fieldConfig.customer.onChange}
          disabled={fieldConfig.customer.disabled}
          isInvalid={fieldConfig.customer.validationErrors.length > 0}
        />
      </Box>
    </Flex>
  )
}
